import { Injectable, OnDestroy } from "@angular/core";
import { AuthService } from "./auth.service";
import { BehaviorSubject, Subscription } from "rxjs";
import { FakturniaNavigationItem } from "@fakturnia/components/navigation/navigation.types";
import { DashboardApiService } from "./api/dashboard.api.service";
import { DashboardCounters } from "shared/models/dashboard-counters";
import { TextUtils } from "shared/utils/text.utils";
import { SettingsService } from "./settings.service";
import { UserSettings } from "shared/models/user-settings.model";

@Injectable({ providedIn: 'root' })
export class NavigationService implements OnDestroy {

    private _subscriptions: Subscription[] = []
    private _isLoggedIn: boolean = false
    private _userSettings: UserSettings | null = null

    private _navigation = new BehaviorSubject<FakturniaNavigationItem[]>([]);
    currentNavigation = this._navigation.asObservable();

    private _plannerUpdatesCount = new BehaviorSubject<number>(0);
    plannerUpdatesCount$ = this._plannerUpdatesCount.asObservable();

    private _counters = new DashboardCounters()
    constructor(
        private _authService: AuthService,
        private _dashboardApiService: DashboardApiService,
        private _settingsService: SettingsService
    ) {

        this._subscriptions.push(
            this._authService.isLoggedIn$.subscribe({
                next: (isLoggedIn: boolean) => {
                    this._isLoggedIn = isLoggedIn

                    this._buildNavigation()
                }
            })
        )

        this._subscriptions.push(
            this._dashboardApiService.counters$.subscribe({
                next: (counters: DashboardCounters) => {
                    this._counters = counters
                    this._buildNavigation()
                }
            })
        )

        this._subscriptions.push(
            this._settingsService.getUserSettings().subscribe({
                next: (settings: UserSettings) => {
                    this._userSettings = settings
                    this._buildNavigation()
                }
            })
        )

    }

    incrementPlannerUpdates(): void {
        this._plannerUpdatesCount.next(this._plannerUpdatesCount.value + 1);
        this._buildNavigation();
    }

    clearPlannerUpdates(): void {
        this._plannerUpdatesCount.next(0);
        this._buildNavigation();
    }

    get plannerUpdatesCount(): number {
        return this._plannerUpdatesCount.value;
    }

    private _buildNavigation() {

        const navigation: FakturniaNavigationItem[] = []
        if (this._isLoggedIn) {
            const user = this._userSettings?.user;
            const isOwner = !user || user.role === 'user';
            
            // Dashboard (Projects)
            const showProjects = isOwner || (user.permissions && user.permissions.includes('VIEW_PROJECT_DETAILS'));
            if (showProjects) {
                navigation.push({
                    id: 'home',
                    title: 'Projects',
                    type: 'basic',
                    icon: 'tabler_outline:report',
                    link: '/account/home'
                });
            }

            navigation.push({
                id: 'spacer',
                type: 'spacer'
            })

            // Manager
            const showManager = isOwner || (user.permissions && (
                user.permissions.includes('VIEW_PROJECT_MANAGEMENT') ||
                user.permissions.includes('VIEW_BILLING') ||
                user.permissions.includes('VIEW_CHECKLIST') ||
                user.permissions.includes('VIEW_FIELD_MEASURE')
            ));
            if (showManager) {
                navigation.push({
                    id: 'manager',
                    title: 'Manager',
                    type: 'basic',
                    icon: 'tabler_outline:device-laptop',
                    link: '/account/manager'
                });
            }

            // Ledger
            const showLedger = isOwner || (user.permissions && (
                user.permissions.includes('FINANCIAL_ANALYSIS') ||
                user.permissions.includes('VIEW_BILLING') ||
                user.permissions.includes('VIEW_TIMESHEET')
            ));
            if (showLedger) {
                navigation.push({
                    id: 'ledger',
                    title: 'Ledger',
                    type: 'basic',
                    icon: 'tabler_outline:report-analytics',
                    link: '/account/ledger'
                });
            }

            // Admin — hide for employees without VIEW_MANAGEMENT permission
            const showManagement = isOwner || (user.permissions && user.permissions.includes('VIEW_MANAGEMENT'));
            if (showManagement) {
                navigation.push({
                    id: 'management',
                    title: 'Admin',
                    type: 'basic',
                    icon: 'tabler_outline:pencil-cog',
                    link: '/account/management'
                })
            }
           
            this._navigation.next(navigation)
            return
        }

        this._navigation.next(navigation)
    }

    ngOnDestroy(): void {
        this._subscriptions.forEach(sub => sub.unsubscribe())
    }
}